<template>
  <div 
    class="group relative bg-casino-card rounded-lg sm:rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-casino-primary/10 border border-white/5 hover:border-casino-primary/30"
    @click="handleCardClick"
  >
    <!-- Image Container - Aspect ratio más cuadrado en móvil -->
    <div class="relative aspect-[4/3] overflow-hidden">
      <img
        :src="game.thumbnail || fallbackImage"
        :alt="game.name"
        class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        @error="handleImageError"
      />
      
      <!-- Gradient Overlay -->
      <div class="absolute inset-0 bg-gradient-to-t from-casino-card via-transparent to-transparent opacity-60"></div>
      
      <!-- Play Button Overlay - Solo en desktop hover -->
      <div class="hidden sm:flex absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 items-center justify-center">
        <button
          class="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 text-white font-bold py-2 px-4 rounded-full flex items-center gap-2 shadow-lg text-sm"
          :class="canPlay ? 'bg-casino-primary hover:bg-casino-primary-light shadow-casino-primary/30' : 'bg-gray-700 hover:bg-gray-600 shadow-black/40'"
        >
          <Play v-if="canPlay" class="w-4 h-4" />
          <Lock v-else class="w-4 h-4" />
          {{ canPlay ? t('gameCard.play') : t('gameCard.login') }}
        </button>
      </div>

      <div v-if="!canPlay" class="absolute top-2 right-2 px-2 py-1 rounded bg-black/70 border border-white/10 text-[10px] uppercase tracking-wide text-gray-200">
        {{ t('gameCard.realOnly') }}
      </div>

      <!-- Badges - Más pequeños en móvil -->
      <div class="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 flex flex-col gap-1">
        <!-- Featured Badge -->
        <div v-if="game.featured" class="px-1.5 py-0.5 sm:px-2 sm:py-1 bg-gradient-to-r from-casino-gold to-yellow-400 text-black text-[9px] sm:text-[10px] font-bold rounded shadow-lg">
          {{ t('gameCard.featured') }}
        </div>
        
        <!-- New Badge -->
        <div v-if="isNew" class="px-1.5 py-0.5 sm:px-2 sm:py-1 bg-purple-500 text-white text-[9px] sm:text-[10px] font-bold rounded shadow-lg">
          {{ t('gameCard.new') }}
        </div>
      </div>

      <!-- Live Indicator -->
      <div v-if="isLive" class="absolute bottom-1.5 left-1.5 sm:bottom-2 sm:left-2">
        <div class="flex items-center gap-1 px-1.5 py-0.5 sm:px-2 sm:py-1 bg-casino-loss/90 text-white text-[9px] sm:text-[10px] font-bold rounded shadow-lg backdrop-blur-sm">
          <span class="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-white rounded-full animate-pulse"></span>
          {{ t('gameCard.live') }}
        </div>
      </div>
    </div>

    <!-- Info Section - Compacta -->
    <div class="p-2 sm:p-3">
      <h3 class="font-semibold text-white text-xs sm:text-sm truncate mb-1 group-hover:text-casino-primary-light transition-colors">
        {{ game.name }}
      </h3>
      
      <div class="flex items-center gap-1.5 flex-wrap">
        <!-- Type Tag -->
        <span class="px-1.5 py-0.5 bg-white/10 text-gray-400 text-[9px] sm:text-[10px] font-medium rounded">
          {{ formatType(game.type) }}
        </span>
        
        <!-- Provider Tag -->
        <span class="px-1.5 py-0.5 bg-casino-primary/20 text-casino-primary-light text-[9px] sm:text-[10px] font-medium rounded truncate max-w-[60px] sm:max-w-[80px]">
          {{ game.provider || t('gameCard.unknownProvider') }}
        </span>
      </div>
    </div>

    <!-- Favorite Button - Visible siempre en móvil, hover en desktop -->
    <button
      @click.stop="toggleFavorite"
      class="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 z-10 p-1.5 sm:p-1.5 rounded-full transition-all duration-200"
      :class="isFavorite 
        ? 'bg-casino-loss/90 text-white' 
        : 'bg-black/50 text-white sm:opacity-0 sm:group-hover:opacity-100 hover:bg-casino-loss/70'"
    >
      <Heart
        class="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-all"
        :class="isFavorite ? 'fill-current' : ''"
      />
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { tEnum } from '@/i18n'
import { useFavoritesStore } from '@/stores/favorites'
import { useNotificationStore } from '@/stores/notifications'
import { useAuthStore } from '@/stores/auth'
import { APP_CONFIG } from '@/config/app'
import { Play, Heart, Lock } from 'lucide-vue-next'

const props = defineProps({
  game: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['play', 'favorite-toggle', 'login-required'])

const { t } = useI18n()
const favoritesStore = useFavoritesStore()
const notificationStore = useNotificationStore()
const authStore = useAuthStore()

const isFavorite = computed(() => favoritesStore.isFavorite(props.game.id))

const fallbackImage = APP_CONFIG.gamePlaceholderImage
const canPlay = computed(() => authStore.isAuthenticated || Boolean(props.game.fun))

const isLive = computed(() => props.game.type?.startsWith('live_'))

const isNew = computed(() => {
  if (!props.game.created_at) return false
  const created = new Date(props.game.created_at)
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  return created > thirtyDaysAgo
})

const handleImageError = (event) => {
  event.target.src = fallbackImage
}

const formatType = (type) => {
  if (!type) return t('gameCard.game')
  const translated = tEnum('gameTypes', type)
  if (translated !== type) return translated
  return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const toggleFavorite = () => {
  const newStatus = favoritesStore.toggleFavorite(props.game.id)

  if (newStatus) {
    notificationStore.favoriteAdded(props.game.name)
  } else {
    notificationStore.favoriteRemoved(props.game.name)
  }

  emit('favorite-toggle', { game: props.game, isFavorite: newStatus })
}

const handleCardClick = () => {
  if (!canPlay.value) {
    emit('login-required', props.game)
    return
  }
  emit('play', props.game)
}
</script>
